import React, { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { FaEnvelope, FaLock, FaSignInAlt } from "react-icons/fa";
import { FaEye, FaEyeSlash } from "react-icons/fa"; // 👁️ icons
import { useToastHelpers } from "../Toasts/ToastUtils";
import { useAuth } from "../Authentication/AuthContext";
import { loginUser } from "../../apis/UserApis";
import SeoMeta from "../SeoMeta";
import ForgotPasswordModal from "./ForgotPasswordModal";
import { useTheme } from "../Theme/ThemeContext";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const { successToast, errorToast } = useToastHelpers();
  const { user, login } = useAuth();
  const { currentTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // Get the redirect path from location state, default to home
  const from = location.state?.from?.pathname || "/";

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError("⚠️ Please fill in all fields.");
      return;
    }
    setError("");
    loginUser({ email, password })
      .then((response) => {
        const { email, userId, role, token } = response.data;
        successToast("Login successful", "Welcome back!");
        login({ email, userId, role, token });
        // Redirect to the original page user was trying to access
        navigate(from, { replace: true });
      })
      .catch((error) => {
        console.error("Login failed:", error);
        errorToast(
          "Login failed. Please try again.",
          error?.response?.data?.message || "Unknown error"
        );
      });
  };

  if (user && user.token) {
    return <Navigate to={from} replace />;
  }

  return (
    <>
      {/* ✅ SEO Meta */}
      <SeoMeta
        title="Login | Admin - CodeArena"
        description="Sign in to CodeArena admin panel to manage jobs, users, and content efficiently."
        url="https://www.code-arena.in/login"
        image="/logo192.png"
        keywords="CodeArena, Admin Login, Job Portal, React Admin"
        type="website"
      />

      <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-neutral-50 via-white to-accent-50/30 dark:from-neutral-900 dark:via-neutral-900 dark:to-neutral-800 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-accent-200/40 dark:bg-accent-900/20 rounded-full filter blur-3xl opacity-40 -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-emerald-200/40 dark:bg-emerald-900/20 rounded-full filter blur-3xl opacity-40 translate-x-1/2 translate-y-1/2"></div>
        
        <div className="relative max-w-md w-full">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className={`w-12 h-12 bg-gradient-to-br ${currentTheme.colors.gradient} rounded-2xl flex items-center justify-center shadow-lg ${currentTheme.colors.glow}`}>
                <span className="text-white font-bold text-2xl">C</span>
              </div>
              <span className={`text-2xl font-bold bg-gradient-to-r ${currentTheme.colors.gradient} bg-clip-text text-transparent`}>
                CodeArena
              </span>
            </Link>
          </div>
          
          <div className="glass rounded-3xl shadow-xl border border-white/20 dark:border-white/10 p-8 sm:p-10">
            {/* Title */}
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white text-center mb-2">
              Welcome Back
            </h1>
            <p className="text-center text-neutral-600 dark:text-neutral-400 mb-8">
              Sign in to continue your learning journey
            </p>

            {/* Error */}
            {error && (
              <div className="mb-6 text-red-600 dark:text-red-400 text-sm font-medium text-center bg-red-50/80 dark:bg-red-900/30 border border-red-200/50 dark:border-red-800/50 px-4 py-3 rounded-xl backdrop-blur-sm">
                {error}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2"
                >
                  Email Address
                </label>
                <div className="relative">
                  <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500 w-4 h-4" />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="ca-input pl-11"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              {/* Password with toggle */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2"
                >
                  Password
                </label>
                <div className="relative">
                  <FaLock className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500 w-4 h-4" />
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"} 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="ca-input pl-11 pr-12"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
                  >
                    {showPassword ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                className={`w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl font-semibold text-white bg-gradient-to-r ${currentTheme.colors.gradient} hover:opacity-90 shadow-lg ${currentTheme.colors.glow} hover:shadow-xl transition-all duration-300 hover:scale-[1.02]`}
              >
                <FaSignInAlt className="w-4 h-4" />
                Sign In
              </button>
            </form>

            {/* Extra Links */}
            <div className="mt-6 flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-neutral-500 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 font-medium transition-colors"
              >
                Forgot password?
              </button>
              <Link
                className="text-accent-600 dark:text-accent-400 hover:text-accent-700 dark:hover:text-accent-300 font-semibold transition-colors"
                to="/signup"
              >
                Create account
              </Link>
            </div>
          </div>

          {/* Back to Home */}
          <div className="text-center mt-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 text-sm font-medium transition-colors"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>

      <ForgotPasswordModal
        show={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
    </>
  );
}

